import { createSelector } from 'reselect'
import { sum } from 'lib/array/arrayUtils'
import { isPurchaseCreditItem } from 'lib/checkout/checkoutUtils'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'

export const getPurchaseCreditItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.PurchaseCreditItem> => items.filter(isPurchaseCreditItem),
  reselectShallowEqual,
)

export const isStandalonePurchaseCredit = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getPurchaseCreditItems(state),
  (items, purchaseCreditItems): boolean => !!items.length && items.length === purchaseCreditItems.length,
)

export const getPurchaseCreditItemsView = createSelector(
  (state: App.State) => getPurchaseCreditItems(state),
  () => getCurrentIntl(),
  (purchaseCreditItems, intl): App.WithDataStatus<Array<App.Checkout.PurchaseCreditItemView>> => {
    return {
      hasRequiredData: true,
      data: purchaseCreditItems.map((item): App.Checkout.PurchaseCreditItemView => {
        return {
          title: intl.formatMessage({
            id: 'zWKhBtZN_C6rL0Zxy1ng',
            defaultMessage: 'Purchase Credit',
            description: 'Title shown for a purchase credit item in checkout item summaries and totals',
          }),
          item,
          loyalty: {},
          totals: {
            price: item.amount,
            value: item.amount,
            memberPrice: 0,
            taxesAndFees: 0,
            memberValue: 0,
            surcharge: 0,
            extraGuestSurcharge: 0,
          },
        }
      }),
    }
  },
)

export const getPurchaseCreditBreakdownView = createSelector(
  (state: App.State) => getPurchaseCreditItemsView(state),
  () => getCurrentIntl(),
  (viewWithStatus, intl): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (viewWithStatus.data.length === 0) { return { hasRequiredData: viewWithStatus.hasRequiredData, data: [] } }
    const breakdownView: App.Checkout.PriceBreakdownView = {
      title: intl.formatMessage({
        id: 'NdWpBQNgt0VVYkeNYIxm',
        defaultMessage: 'Purchase Credit',
        description: 'Heading shown in the checkout price breakdown for standalone purchase credit items',
      }),
      price: sum(viewWithStatus.data, item => item.totals.price || 0),
      memberPrice: 0,
      additionalInfoText: [],
      items: [],
    }

    return {
      hasRequiredData: true,
      data: [breakdownView],
    }
  },
)
